/**
 * SQL 빌더의 「요소 추가·삭제」(2026-10-19) — 트리를 고치지 않고 **원문의 한 자리에 형식 텍스트를 넣거나 범위를 지운다**.
 * 결과는 { sql, focus } 다. focus 는 새로 생긴 요소의 시작 오프셋(다시 파싱한 뒤 그 자리의 요소를 고른다).
 * 형식 텍스트는 SQL 이므로 번역하지 않는다(column_name · table_name · condition 은 자리표시).
 */
const CLAUSE_ORDER = ['WITH', 'SELECT', 'FROM', 'WHERE', 'START WITH', 'CONNECT BY', 'GROUP BY', 'HAVING', 'ORDER BY', 'OFFSET', 'FETCH', 'LIMIT', 'FOR UPDATE'];
const CLAUSE_TEMPLATE = {
    FROM: 'FROM table_name',
    WHERE: 'WHERE condition',
    'GROUP BY': 'GROUP BY column_name',
    HAVING: 'HAVING COUNT(*) > 1',
    'ORDER BY': 'ORDER BY 1',
    'CONNECT BY': 'CONNECT BY PRIOR id = parent_id',
    'START WITH': 'START WITH parent_id IS NULL',
};
export const ADDABLE_CLAUSES = Object.keys(CLAUSE_TEMPLATE);

/** 그 자리 줄의 들여쓰기(공백 수) */
function columnOf(src, offset) {
    const nl = src.lastIndexOf('\n', offset - 1);
    return offset - (nl + 1);
}
const pad = (n) => ' '.repeat(Math.max(0, n));
const splice = (src, at, del, text) => src.slice(0, at) + text + src.slice(at + del);

/** 질의에 빠진 절을 정해진 순서의 자리에 넣는다 */
export function addClause(src, q, type) {
    if (!CLAUSE_TEMPLATE[type] || q.clauses.some(c => c.type === type)) return null;
    const rank = CLAUSE_ORDER.indexOf(type);
    const after = q.clauses.filter(c => CLAUSE_ORDER.indexOf(c.type) < rank).pop();
    const before = q.clauses.find(c => CLAUSE_ORDER.indexOf(c.type) > rank);
    const indent = columnOf(src, (after || before || q).start);
    if (after) {
        const at = after.end;
        const text = `\n${pad(indent)}${CLAUSE_TEMPLATE[type]}`;
        return { sql: splice(src, at, 0, text), focus: at + 1 + indent };
    }
    const at = before ? before.start : q.start;
    const text = `${CLAUSE_TEMPLATE[type]}\n${pad(indent)}`;
    return { sql: splice(src, at, 0, text), focus: at };
}

/** SELECT 에 스칼라 서브쿼리 */
export function addScalar(src, selectClause) {
    const indent = columnOf(src, selectClause.bodyStart);
    const inner = indent + 1;
    const text = `,\n${pad(indent)}(SELECT column_name\n${pad(inner + 2)}FROM table_name\n${pad(inner + 1)}WHERE condition) AS new_col`;
    const at = selectClause.bodyEnd;
    return { sql: splice(src, at, 0, text), focus: at + 2 + indent };
}

/** FROM 에 인라인뷰 */
export function addInline(src, fromClause, n = 1) {
    const indent = columnOf(src, fromClause.bodyStart);
    const text = `,\n${pad(indent)}(SELECT column_name\n${pad(indent + 3)}FROM table_name) v${n}`;
    const at = fromClause.bodyEnd;
    return { sql: splice(src, at, 0, text), focus: at + 2 + indent };
}

/** WHERE 에 EXISTS · IN 조건 — WHERE 가 없으면 만든다 */
export function addCondition(src, q, kind) {
    const where = q.clauses.find(c => c.type === 'WHERE');
    const lead = kind === 'in' ? 'column_name IN ' : 'EXISTS ';
    /** 조건 본문 — 여는 괄호가 col 열에 올 때 SELECT 아래로 FROM·WHERE 를 맞춘다 */
    const bodyAt = (col) => {
        const sel = col + lead.length + 1;   // ( 다음 SELECT 의 열
        return kind === 'in'
            ? `${lead}(SELECT column_name\n${pad(sel + 2)}FROM table_name)`
            : `${lead}(SELECT 1\n${pad(sel + 2)}FROM table_name\n${pad(sel + 1)}WHERE condition)`;
    };
    if (!where) {
        const made = addClause(src, q, 'WHERE');
        if (!made) return null;
        // 방금 넣은 「WHERE condition」 의 condition 자리를 바꾼다
        const condAt = made.sql.indexOf('condition', made.focus);
        const sql = splice(made.sql, condAt, 'condition'.length, bodyAt(columnOf(made.sql, condAt)));
        return { sql, focus: sql.indexOf('(', condAt) };
    }
    const indent = Math.max(0, columnOf(src, where.start) + 2);
    const text = `\n${pad(indent)}AND ${bodyAt(indent + 4)}`;
    const at = where.end;
    const sql = splice(src, at, 0, text);
    return { sql, focus: sql.indexOf('(', at + 1 + indent + 4) };
}

/** WITH 에 CTE — WITH 가 없으면 질의 앞에 만든다 */
export function addCte(src, q, n = 1) {
    const withClause = q.clauses.find(c => c.type === 'WITH');
    const name = `cte${n}`;
    const body = `${name} AS (\n  SELECT column_name\n    FROM table_name\n)`;
    if (withClause) {
        const at = withClause.end;
        const text = `,\n${body}`;
        return { sql: splice(src, at, 0, text), focus: at + 2 };
    }
    const at = q.start;
    return { sql: splice(src, at, 0, `WITH ${body}\n`), focus: at + 5 };
}

/** 질의 끝에 UNION ALL 가지 */
export function addUnion(src, q) {
    const at = q.end;
    const text = '\nUNION ALL\nSELECT column_name\n  FROM table_name';
    return { sql: splice(src, at, 0, text), focus: at + '\nUNION ALL\n'.length };
}

/** 항목 하나를 구분자(쉼표·AND·OR)와 함께 지운다 */
function removeItem(src, items, idx) {
    const it = items[idx];
    if (items.length === 1) return null;
    if (idx === 0) {
        const next = items[1];
        return { sql: splice(src, it.start, next.start - it.start, ''), focus: it.start };
    }
    const prev = items[idx - 1];
    return { sql: splice(src, prev.end, it.end - prev.end, ''), focus: prev.start };
}

/**
 * 요소 지우기 — 서브쿼리는 그것이 든 항목째(쉼표·AND 함께), 절은 절째, CTE 는 목록에서. 지울 수 없으면 null
 * @param parent 지울 질의를 품은 절(서브쿼리·CTE) 또는 절을 품은 질의(절)
 */
export function removeNode(src, node, parent, nodeType) {
    if (nodeType === 'clause') {
        if (node.type === 'SELECT') return null;
        // 앞쪽 공백·줄바꿈째 지운다
        let s = node.start;
        while (s > 0 && /\s/.test(src[s - 1])) s--;
        return { sql: splice(src, s, node.end - s, ''), focus: Math.max(0, s - 1) };
    }
    if (!parent || !parent.items) return null;
    const idx = parent.items.findIndex(it => node.outerStart >= it.start && node.outerEnd <= it.end);
    if (idx < 0) return null;
    if (parent.items.length === 1) {
        // 하나뿐이면 절째 지운다(WITH·WHERE). SELECT·FROM 의 유일한 항목은 지우지 않는다
        if (parent.type === 'WITH' || parent.type === 'WHERE' || parent.type === 'HAVING') return removeNode(src, parent, null, 'clause');
        return null;
    }
    return removeItem(src, parent.items, idx);
}
